import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

import { useAlert } from '../../providers/AlertProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { 
    fetch_pending_holiday_requests, 
    update_holiday_request
} from '../../api/holidayRequests';
import { fetch_all_holidays, use_delete_holiday, use_add_new_holiday, delete_holiday_by_user_date, find_holiday_by_user_date } from '../../api/holidays';

import '../css/AddService.css';
import { width } from '@fortawesome/free-regular-svg-icons/faAddressBook';

const HolidayRequestsModal = ({ theme, show, onHide, onRequestsUpdated, holidaysData = [], setHolidaysData, pendingRequests = null }) => {
    const { showAlert } = useAlert();
    const { profile, isManager, isDev, permissions } = useAuthProvider();
    const canEdit = isDev || permissions.perm_holidays_edit;
    
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [processingId, setProcessingId] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingRequestId, setRejectingRequestId] = useState(null);
    const [rejectionComment, setRejectionComment] = useState('');

    const isDark = theme === 'dark';

    const fetchRequests = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch_pending_holiday_requests();
            if (response.success) {
                setRequests(response.data || []);
            } else {
                showAlert({
                    message: 'Failed to fetch holiday requests',
                    type: 'danger'
                });
            }
        } catch (error) {
            console.error('Error fetching holiday requests:', error);
            showAlert({
                message: 'Error fetching holiday requests',
                type: 'danger'
            });
        } finally {
            setIsLoading(false);
        }
    }, [showAlert]);

    // Use passed pendingRequests if available, otherwise fetch
    useEffect(() => {
        if (show) {
            if (pendingRequests) {
                setRequests(pendingRequests);
            } else {
                fetchRequests();
            }
        }
    }, [show, pendingRequests, fetchRequests]);

    const getStatusColor = (status) => {
        const colors = {
            'pending': '#e67e22', // Orange instead of yellow for better contrast
            'cancel': '#dc3545'
        };
        return colors[status] || '#6c757d';
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch (error) {
            return dateString;
        }
    };

    if (!show) return null;

    const handleApprove = async (requestId) => {
        setProcessingId(requestId);
        
        // Find the request to get its details
        const request = requests.find(r => r.id === requestId);
        if (!request) {
            showAlert({
                message: 'Request not found',
                type: 'danger'
            });
            setProcessingId(null);
            return;
        }

        let holidayWasDeleted = false; // Track if holiday was actually deleted

        try {
            if (request.status === 'pending') {
                // For pending requests: create holiday entry first, then approve
                const holidayData = {
                    userId: request.profile_id,
                    holidayType: request.request_reason,
                    notes: request.request_comments || '',
                    startDate: request.request_date,  // Single date for both start and end
                    endDate: request.request_date     // Same date since it's a single day request
                };

                const holidayResponse = await use_add_new_holiday(holidayData);
                
                if (holidayResponse.status !== 200 && holidayResponse.status !== 201) {
                    showAlert({
                        message: 'Failed to create holiday entry',
                        type: 'danger'
                    });
                    setProcessingId(null);
                    return;
                }
                
                // On success, add the holiday to state using the data we sent
                if (setHolidaysData) {
                    const newHoliday = {
                        id: `temp_${Date.now()}`, // Temporary ID until next fetch
                        user_id: holidayData.userId,
                        holiday_type: holidayData.holidayType,
                        date: holidayData.startDate, // Since startDate = endDate for single day
                        notes: holidayData.notes,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };
                    
                    setHolidaysData(prevHolidays => [...prevHolidays, newHoliday]);
                }
            } else if (request.status === 'cancel') {
                // For cancelled requests: 
                // 1. Try to find and delete any existing holiday (optional - may not exist)
                // 2. Always update request status to approved (approve the cancellation)
                // 3. Update frontend state if holiday was found and deleted
                
                try {
                    // Step 1: Try to find and delete holiday (this is optional)
                    const findResponse = await find_holiday_by_user_date(request.profile_id, request.request_date);
                    
                    if (findResponse.status === 200) {
                        const holidaysData = findResponse.data?.data?.holidays || [];
                        
                        if (holidaysData && holidaysData.length > 0) {
                            const existingHoliday = holidaysData[0];
                            
                            const deleteResponse = await use_delete_holiday(existingHoliday.id);
                            
                            if (deleteResponse.status === 200) {
                                holidayWasDeleted = true;
                            }
                        }
                    }
                    
                    // Note: We don't throw errors here because it's OK if no holiday exists
                    
                } catch (error) {
                    // Log the error but don't stop the process
                    console.log('Holiday deletion failed (this may be expected):', error.message);
                }
                
                // Step 2: Always proceed to approve the cancellation request
                // (This happens after this block in the main flow)
            }

            // Now approve the request
            const response = await update_holiday_request(
                requestId, 
                'approved', 
                '', // No comments needed for approval
                profile?.id
            );

            // Now approve the request
            const updateResponse = await update_holiday_request(
                requestId, // Keep as integer
                request.status === 'cancel' ? 'cancelled' : 'approved', 
                '', // No comments needed for approval
                profile?.id
            );

            if (updateResponse.success) {
                // Update frontend state after successful approval
                if (request.status === 'cancel' && holidayWasDeleted && setHolidaysData) {
                    // Remove the holiday from state only if it was actually deleted
                    setHolidaysData(prevHolidays => 
                        prevHolidays.filter(h => 
                            !(h.user_id === request.profile_id && h.date === request.request_date)
                        )
                    );
                }
                
                showAlert({
                    message: request.status === 'pending' 
                        ? 'Holiday request approved and holiday created' 
                        : holidayWasDeleted 
                            ? 'Holiday cancellation approved and holiday removed'
                            : 'Holiday cancellation approved (no existing holiday found)',
                    type: 'success'
                });
                
                // Refresh the requests
                if (pendingRequests) {
                    onRequestsUpdated && onRequestsUpdated();
                } else {
                    fetchRequests();
                }
            } else {
                // Even if request update fails, update state for cancelled requests if holiday was deleted
                if (request.status === 'cancel' && holidayWasDeleted && setHolidaysData) {
                    setHolidaysData(prevHolidays => 
                        prevHolidays.filter(h => 
                            !(h.user_id === request.profile_id && h.date === request.request_date)
                        )
                    );
                    
                    showAlert({
                        message: 'Holiday removed successfully, but failed to update request status. Please refresh the page.',
                        type: 'warning'
                    });
                } else {
                    showAlert({
                        message: request.status === 'cancel' 
                            ? 'Failed to update cancellation request status'
                            : 'Failed to approve holiday request',
                        type: 'danger'
                    });
                }
            }
        } catch (error) {
            console.error('Error approving request:', error);
            showAlert({
                message: 'Error approving holiday request',
                type: 'danger'
            });
        } finally {
            setProcessingId(null);
        }
    };

    const handleReject = async (requestId) => {
        setRejectingRequestId(requestId);
        setShowRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectionComment.trim()) {
            showAlert({
                message: 'Please provide a reason for rejecting this request',
                type: 'warning'
            });
            return;
        }
        
        setProcessingId(rejectingRequestId);
        setShowRejectModal(false);
        
        try {
            const response = await update_holiday_request(
                rejectingRequestId, 
                'rejected', 
                rejectionComment, 
                profile?.id
            );

            if (response.success) {
                showAlert({
                    message: 'Holiday request rejected',
                    type: 'warning'
                });
                
                // Refresh the requests
                if (pendingRequests) {
                    onRequestsUpdated && onRequestsUpdated();
                } else {
                    fetchRequests();
                }
                
                // Reset rejection state
                setRejectionComment('');
                setRejectingRequestId(null);
            } else {
                showAlert({
                    message: 'Failed to reject holiday request',
                    type: 'danger'
                });
            }
        } catch (error) {
            console.error('Error rejecting request:', error);
            showAlert({
                message: 'Error rejecting holiday request',
                type: 'danger'
            });
        } finally {
            setProcessingId(null);
        }
    };

    const cancelReject = () => {
        setShowRejectModal(false);
        setRejectingRequestId(null);
        setRejectionComment('');
    };

    return (
        <div id={theme} className="overlay">
            <div className="modal-container" style={{ maxWidth: '95vw', width: '1200px' }}>
                <h2>Holiday Requests</h2>
                
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>
                ) : requests.length === 0 ? (
                    <div style={{ 
                        padding: '20px', 
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 123, 255, 0.1)',
                        borderRadius: '4px',
                        color: isDark ? '#fff' : '#333'
                    }}>
                        No pending holiday requests found.
                    </div>
                ) : (
                    <div style={{ 
                        maxHeight: '400px', 
                        overflowY: 'auto',
                        border: isDark ? '1px solid #555' : '1px solid #ddd',
                        borderRadius: '4px'
                    }}>
                        <table style={{ 
                            width: '100%', 
                            borderCollapse: 'collapse',
                            backgroundColor: isDark ? '#2d2d2d' : '#fff'
                        }}>
                            <thead>
                                <tr style={{ 
                                    backgroundColor: isDark ? '#444' : '#f8f9fa',
                                    borderBottom: isDark ? '1px solid #555' : '1px solid #ddd'
                                }}>
                                    <th style={{ padding: '12px', textAlign: 'left', color: isDark ? '#fff' : '#333', width: '15%' }}>Employee</th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: isDark ? '#fff' : '#333', width: '12%' }}>Date</th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: isDark ? '#fff' : '#333', width: '15%' }}>Type</th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: isDark ? '#fff' : '#333', width: '35%' }}>Comments</th>
                                    <th style={{ padding: '12px', textAlign: 'left', color: isDark ? '#fff' : '#333', width: '10%' }}>Status</th>
                                    <th style={{ padding: '12px', textAlign: 'center', color: isDark ? '#fff' : '#333', width: '13%' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((request, index) => (
                                    <tr key={request.id} style={{ 
                                        borderBottom: isDark ? '1px solid #555' : '1px solid #eee',
                                        backgroundColor: index % 2 === 0 ? 'transparent' : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)')
                                    }}>
                                        <td style={{ padding: '12px', color: isDark ? '#fff' : '#333' }}>
                                            {request.requested_name || 'Unknown User'}
                                        </td>
                                        <td style={{ padding: '12px', color: isDark ? '#fff' : '#333' }}>
                                            {formatDate(request.request_date)}
                                        </td>
                                        <td style={{ padding: '12px', color: isDark ? '#fff' : '#333' }}>
                                            {request.request_reason?.replace('_', ' ') || 'N/A'}
                                        </td>
                                        <td style={{ padding: '12px', color: isDark ? '#fff' : '#333' }}>
                                            {request.request_comments || '-'}
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                backgroundColor: getStatusColor(request.status),
                                                color: '#fff'
                                            }}>
                                                {request.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            <div style={{ display: 'flex', gap: '4px', alignContent: 'center', justifyContent: 'center' }}>
                                                {canEdit && (
                                                    <>
                                                <button
                                                    onClick={() => handleApprove(request.id)}
                                                    disabled={processingId === request.id}
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor: '#28a745',
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: '3px',
                                                        cursor: processingId === request.id ? 'not-allowed' : 'pointer',
                                                        opacity: processingId === request.id ? 0.6 : 1,
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faCheck} style={{ width: '32px', height: '14px' }} />
                                                </button>
                                                {request.status !== 'cancel' && (
                                                    <button
                                                        onClick={() => handleReject(request.id)}
                                                        disabled={processingId === request.id}
                                                        style={{
                                                            padding: '4px 8px',
                                                            backgroundColor: '#dc3545',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '3px',
                                                            cursor: processingId === request.id ? 'not-allowed' : 'pointer',
                                                            opacity: processingId === request.id ? 0.6 : 1,
                                                            fontSize: '12px'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faTimes} style={{ width: '32px', height: '14px' }} />
                                                    </button>
                                                )}
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="button-group">
                    <button onClick={onHide} className="close-button">
                        Close
                    </button>
                </div>
            </div>
            
            {/* Rejection Comment Modal - Outside main modal */}
            {showRejectModal && (
                <div id={theme} className="overlay" style={{ zIndex: 30 }}>
                    <div className="modal-container" style={{ 
                        maxWidth: '500px', 
                        width: '90vw'
                    }}>
                        <h3>Reject Holiday Request</h3>
                        <p style={{ color: isDark ? '#ccc' : '#666', marginBottom: '15px' }}>
                            Please provide a reason for rejecting this request:
                        </p>
                        
                        <textarea
                            value={rejectionComment}
                            onChange={(e) => setRejectionComment(e.target.value)}
                            placeholder="Enter rejection reason..."
                            rows={4}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: isDark ? '1px solid #555' : '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#fff',
                                color: isDark ? '#fff' : '#333',
                                resize: 'vertical',
                                minHeight: '100px',
                                marginBottom: '15px'
                            }}
                        />
                        
                        <div className="button-group">
                            <button 
                                onClick={confirmReject} 
                                className="submit-button"
                                disabled={!rejectionComment.trim()}
                                style={{
                                    opacity: !rejectionComment.trim() ? 0.6 : 1,
                                    cursor: !rejectionComment.trim() ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Reject Request
                            </button>
                            <button onClick={cancelReject} className="close-button">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HolidayRequestsModal;